/**
 * Calculate carbon performance metrics comparing targets vs actuals
 */

import { toJS } from "mobx";

/**
 * Sum all fields in a scope object
 */
const sumTargetFields = (scopeObj) => {
  if (!scopeObj) return 0;
  return Object.values(scopeObj).reduce(
    (sum, val) => sum + (Number(val) || 0),
    0,
  );
};

/**
 * Determine performance status based on progress percentage
 * Updated thresholds for more realistic status assessment
 */
const getPerformanceStatus = (progress) => {
  if (progress < 80) return "at-risk"; // Less than 80% of target - need attention
  if (progress >= 80 && progress <= 110) return "on-track"; // 80-110% - acceptable range
  if (progress > 110) return "over-target"; // More than 110% - over budget
  return "at-risk";
};

/**
 * Check if submission is usable for aggregation
 */
const isSubmissionUsable = (submission) => {
  return (
    submission.status === "submitted" &&
    submission.bankReview?.status === "approved" &&
    submission.consent?.status === "approved"
  );
};

/**
 * Calculate activity-level breakdown from submissions
 */
const scope1KeyMap = {
  stationaryCombustion: "stationary",
  mobileCombustion: "mobile",
  fugitiveEmissions: "fugitive",
  processEmissions: "process",
};

const scope2KeyMap = {
  purchasedElectricity: "electricity",
  purchasedSteam: "steam",
  purchasedCooling: "cooling",
};
const calculateActivityBreakdown = (targets, submissions) => {
  const breakdown = {
    scope1: {
      stationaryCombustion: 0,
      mobileCombustion: 0,
      fugitiveEmissions: 0,
      processEmissions: 0,
    },
    scope2: {
      purchasedElectricity: 0,
      purchasedSteam: 0,
      purchasedCooling: 0,
    },
  };

  // This would require submission data to include activity-level details
  // For now, we'll distribute totals proportionally based on targets
  submissions.forEach((sub) => {
    // if (!isSubmissionUsable(sub)) return;
    let scope1Total = 0;
    let scope2Total = 0;

    if (sub?.data?.scope1?.mode === "activities") {
    } else {
      scope1Total = sub.totals?.scope1TotalTco2e || 0;
    }
    if (sub?.data?.scope2?.mode === "activities") {
    } else {
      scope2Total = sub.totals?.scope2TotalTco2e || 0;
    }

    // Distribute proportionally if we have target ratios
    if (targets?.scope1) {
      const totalScope1Target = sumTargetFields(targets.scope1);
      if (totalScope1Target > 0) {
        Object.keys(breakdown.scope1).forEach((key) => {
          const activityKey = scope1KeyMap[key];
          if (sub?.data?.scope1?.mode === "activities") {
            breakdown.scope1[key] +=
              Number(
                sub?.data?.scope1?.activities?.[activityKey]?.entries?.[0]
                  ?.quantity,
              ) || 0;
          } else {
            const ratio = (targets?.scope1?.[key] || 0) / totalScope1Target;
            breakdown.scope1[key] += scope1Total * ratio;
          }
        });
      }
    }

    if (targets?.scope2) {
      const totalScope2Target = sumTargetFields(targets.scope2);
      if (totalScope2Target > 0) {
        Object.keys(breakdown.scope2).forEach((key) => {
          const activityKey = scope2KeyMap[key];
          if (sub?.data?.scope2?.mode === "activities") {
            breakdown.scope2[key] +=
              Number(
                sub?.data?.scope2?.activities?.[activityKey]?.entries?.[0]
                  ?.quantity,
              ) || 0;
          } else {
            const ratio = (targets?.scope2?.[key] || 0) / totalScope2Target;
            breakdown.scope2[key] += scope2Total * ratio;
          }
        });
      }
    }
  });

  return breakdown;
};

/**
 * Main function to calculate carbon performance
 */
export const calculateCarbonPerformance = (targets, overview, submissions) => {
  if (!overview || !submissions) {
    return null;
  }

  // Scope 1 calculations
  const scope1Target = sumTargetFields(targets?.scope1 || 0);
  const scope1Actual = overview.aggregated?.scope1TotalTco2e || 0;
  const scope1Variance = scope1Actual - scope1Target;
  const scope1Progress =
    scope1Target > 0 ? (scope1Actual / scope1Target) * 100 : 0;
  const scope1Status = getPerformanceStatus(scope1Progress);

  // Scope 2 calculations
  const scope2Target = sumTargetFields(targets?.scope2 || 0);
  const scope2Actual = overview.aggregated?.scope2TotalTco2e || 0;
  const scope2Variance = scope2Actual - scope2Target;
  const scope2Progress =
    scope2Target > 0 ? (scope2Actual / scope2Target) * 100 : 0;
  const scope2Status = getPerformanceStatus(scope2Progress);

  // Activity-level breakdown
  const activityBreakdown = calculateActivityBreakdown(targets, submissions);

  // SME contributions
  const smeContributions = submissions.map((sub) => ({
    _id: sub.id,
    smeId: sub.organizationUnit?.id,
    smeName: sub.organizationUnit?.name || "Unknown",
    scope1: sub.totals?.scope1TotalTco2e || 0,
    scope2: sub.totals?.scope2TotalTco2e || 0,
    reviewStatus: sub.bankReview?.status || "pending",
    consentStatus: sub.consent?.status || "none",
    isUsable: isSubmissionUsable(sub),
    year: sub.reportingYear || sub.year, // Include year from submission
  }));

  return {
    scope1: {
      target: scope1Target,
      actual: scope1Actual,
      variance: scope1Variance,
      progress: scope1Progress,
      status: scope1Status,
    },
    scope2: {
      target: scope2Target,
      actual: scope2Actual,
      variance: scope2Variance,
      progress: scope2Progress,
      status: scope2Status,
    },
    activityBreakdown,
    smeContributions,
    totalSMEs: submissions.length,
    approvedSMEs: submissions.filter((s) => s.bankReview?.status === "approved")
      .length,
  };
};
